<template>
    <div class="dob-container">
        <!-- Reuses the identity-vault CustomDatePicker so the business-side
             experience matches the consumer-side. The picker owns its own
             header / Cancel + Apply buttons, so this modal is just a thin
             wrapper that handles save + close. -->
        <CustomDatePicker
            :initial-date="initialDob"
            :is-applying="isLoading"
            @cancel="closeDialog"
            @apply="handleApply($event)"
        />
    </div>
</template>

<script setup lang="ts">
import CustomDatePicker from '~/components/main/shared/CustomDatePicker.vue';
import { businessService } from '~/services/business-service';
import { ModalNames } from '~/types/modals';
import { memberFacingMessage } from '~/utils/errors';

const dialogRef = getDialogRef(ModalNames.EDIT_EMPLOYEE_DOB);
const { showSuccess, showError } = useGlobalToast();
const isLoading = ref(false);

const companyId = computed(() => dialogRef.value?.data?.companyId);
const employeeId = computed(() => dialogRef.value?.data?.employeeId);
const employee = computed(() => dialogRef.value?.data?.employee);

// Parse YYYY-MM-DD as noon local time so a date stored at UTC midnight
// doesn't shift back a day in western timezones — matches IV's pattern.
const initialDob = computed<Date>(() => {
    const dob = employee.value?.dob;
    if (dob && dob !== 'RESTRICTED') {
        return new Date(dob + 'T12:00:00');
    }
    // Default to today shifted back 30 years so the picker lands somewhere
    // reasonable when no DOB is set yet.
    const fallback = new Date();
    fallback.setFullYear(fallback.getFullYear() - 30);
    return fallback;
});

const closeDialog = () => {
    dialogRef.value.close();
};

const handleApply = async (selected: Date) => {
    isLoading.value = true;
    try {
        // Anchor at noon local so the YYYY-MM-DD wire format doesn't drift.
        const noon = new Date(
            selected.getFullYear(),
            selected.getMonth(),
            selected.getDate(),
            12,
            0,
            0,
        );
        const dobString = `${noon.getFullYear()}-${String(noon.getMonth() + 1).padStart(2, '0')}-${String(noon.getDate()).padStart(2, '0')}`;
        await businessService.updateEmployee(companyId.value, employeeId.value, {
            dob: dobString as any,
        });
        showSuccess('Success', 'Date of birth updated successfully');
        dialogRef.value.close({ updated: true });
    } catch (error) {
        showError('Error', memberFacingMessage(error, 'Failed to update date of birth'));
    } finally {
        isLoading.value = false;
    }
};
</script>

<style scoped lang="scss">
.dob-container {
    @apply rounded-md sm:p-6 p-0 flex flex-col gap-6;
}
</style>
